import axios from 'axios'
import { FieldSafetyNoticeMetadata } from '@core/domain/models/reviewItemMetadata/fieldSafetyNoticeMetadata'
import { Review } from '@core/domain/models/review.model'
import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import { Prettify } from '@app/utils/prettifyType'
import { generatePmsSearchQuery } from '@app/utils/generateSearchQuery/generators/pms'

type FieldSafetyNoticeResult = {
  id: string
  title?: string
  date?: string
  link?: string
  summary?: string
  source?: string
  fulltext?: string
  type: string
  pdfLinks?: string[]
  reference?: string
}

export class FsnApiClient {
  private readonly baseUrl =
    location.protocol + '//' + location.host + '/safety-information'

  async search({
    query,
    pagination = {
      limit: 5000,
      offset: 0,
    },
    sources,
    filters,
  }: {
    query: string
    sources: string[]
    pagination?: { limit: number; offset: number }
    filters?: { startDate?: string; endDate?: string }
  }): Promise<{ results: FieldSafetyNoticeMetadata[]; totalFound: number }> {
    if (!query) return { results: [], totalFound: 0 }

    const { results, totalFound } = await this.findSafetyInformation({
      query,
      sources,
      pagination,
      filters,
    })

    return {
      results: this.mapResultsToFieldSafetyNotices(results),
      totalFound,
    }
  }

  async createReviewSearches({
    review,
    safetyInformationSourceIds,
  }: {
    review: Review
    safetyInformationSourceIds: string[]
  }): Promise<
    {
      query?: string
      filters?: string
      date: string
      reviewId: number
      importSourceId: string
      items: Prettify<
        FieldSafetyNoticeMetadata & { type: ReviewItemType.FieldSafetyNotice }
      >[]
      queryTranslation?: string
      url?: string
    }[]
  > {
    if (!review.plan) {
      throw new Error('Review plan is required to create a search')
    }
    const query = generatePmsSearchQuery({ plan: review.plan })
    const { results } = await this.search({
      query,
      sources: safetyInformationSourceIds,
    })
    const safetyInformationBySource = new Map<
      string,
      FieldSafetyNoticeMetadata[]
    >()
    safetyInformationSourceIds.forEach((source) => {
      safetyInformationBySource.set(source, [])
    })
    results.forEach((item) => {
      const source = item.source!
      safetyInformationBySource.get(source)?.push(item)
    })
    const searches: {
      query?: string
      filters?: string
      date: string
      reviewId: number
      importSourceId: string
      items: Prettify<
        FieldSafetyNoticeMetadata & { type: ReviewItemType.FieldSafetyNotice }
      >[]
      queryTranslation?: string
      url?: string
    }[] = []
    for (const [source, items] of safetyInformationBySource) {
      searches.push({
        query,
        date: new Date().toISOString(),
        reviewId: review.id,
        importSourceId: source,
        items: items.map((a) => ({
          ...a,
          type: ReviewItemType.FieldSafetyNotice,
        })),
      })
    }
    return searches
  }

  private async findSafetyInformation({
    pagination,
    query,
    sources,
    filters,
  }: {
    query: string
    sources: string[]
    pagination: { limit: number; offset: number }
    filters?: { startDate?: string; endDate?: string }
  }): Promise<{ results: FieldSafetyNoticeResult[]; totalFound: number }> {
    try {
      const solrQuery = `(title:(${encodeURIComponent(query)})) OR (summary:(${encodeURIComponent(query)})) OR (fulltext:(${encodeURIComponent(query)}))`
      const sourceFilter = sources
        .map((a) => `source:"${encodeURIComponent(a)}"`)
        .join(' OR ')
      const fq = sourceFilter ? `&fq=${sourceFilter}` : ''

      let solrUrl = `${this.baseUrl}/?q=${solrQuery}${fq}&start=${pagination.offset}&rows=${pagination.limit}&wt=json`
      if (filters?.startDate && filters?.endDate) {
        const startDateValue = new Date(filters.startDate).toISOString()
        const endDateValue = new Date(filters.endDate).toISOString()
        solrUrl += `&fq=date:[${startDateValue} TO ${endDateValue}]`
      } else if (filters?.startDate) {
        const date = new Date(filters.startDate).toISOString()
        solrUrl += `&fq=date:[${date} TO *]`
      } else if (filters?.endDate) {
        const date = new Date(filters.endDate).toISOString()
        solrUrl += `&fq=date:[* TO ${date}]`
      }
      const { data } = await axios.get(solrUrl)
      return {
        results: data.results as FieldSafetyNoticeResult[],
        totalFound: data.total,
      }
    } catch (error) {
      console.error('Error searching documents:', error)
      return {
        results: [],
        totalFound: 0,
      }
    }
  }

  private mapResultsToFieldSafetyNotices(
    results: FieldSafetyNoticeResult[],
  ): FieldSafetyNoticeMetadata[] {
    return results?.map(
      (result) =>
        new FieldSafetyNoticeMetadata({
          reference: result.reference,
          title: result.title,
          date: result.date,
          link: result.link,
          summary: result.summary,
          source: result.source,
          fulltext: result.fulltext,
          fsnType: result.type,
          potentialPdfUrl: result.pdfLinks?.[0],
        }),
    )
  }
}
